import type { FullEvent } from "../shared/FullEvent";
import { normalizeEventLanguage } from "../constants/languages";

export type DevCommentBaseline = {
  contentFp: string;
  devComment: string;
};

/** Stable JSON fingerprint of event payload excluding dev_comment (for “content unchanged” checks). */
export function contentWithoutDevCommentFingerprint(fd: FullEvent): string {
  const attrs = { ...(fd.attributes ?? {}) } as Record<string, unknown>;
  delete attrs.dev_comment;

  const descriptions = [...(fd.event_descriptions ?? [])]
    .map((d) => ({
      language_code: normalizeEventLanguage(d.language_code),
      label: d.label ?? "",
      label_long: d.label_long ?? "",
      description: d.description ?? "",
      situation: d.situation ?? "",
      actions: d.actions ?? [],
    }))
    .sort((a, b) => a.language_code.localeCompare(b.language_code));

  const core = {
    event_type: fd.event_type,
    event_contract_name: fd.event_contract_name,
    event_code: fd.event_code,
    plant_specific: fd.plant_specific,
    event_class: fd.event_class,
    module: fd.module,
    identifier: fd.identifier,
    attributes: attrs,
    event_descriptions: descriptions,
  };

  return JSON.stringify(core);
}

export function captureDevCommentBaseline(fd: FullEvent): DevCommentBaseline {
  return {
    contentFp: contentWithoutDevCommentFingerprint(fd),
    devComment: String(fd.attributes?.dev_comment ?? ""),
  };
}

/** True if dev_comment text changed and nothing else (export-relevant) changed vs baseline. */
export function isOnlyDevCommentChanged(
  fd: FullEvent,
  baseline: DevCommentBaseline | null
): boolean {
  if (!baseline || !fd.global_event_id) {
    return false;
  }
  const now = String(fd.attributes?.dev_comment ?? "").trim();
  const was = baseline.devComment.trim();
  if (now === was) {
    return false;
  }
  return contentWithoutDevCommentFingerprint(fd) === baseline.contentFp;
}
